import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  styled
} from '@mui/material';
import {
  ResponsiveImage,
  fields
} from 'iron-forge-react-modules';
import {
  useForm,
  useWatch
} from 'react-hook-form';
import {
  FormModal,
  appValidationUtil,
  BulkReceiveSpecificReturnAuthorizationsModal
} from 'modules';
import {
  Add,
  SwapHoriz
} from '@mui/icons-material';

import * as dataService from './dataService';
import * as types from './types';

import imgInvitationIllustration from 'assets/images/invitationIllustration/1x.png';
import imgInvitationIllustration2x from 'assets/images/invitationIllustration/2x.png';
import imgInvitationIllustration3x from 'assets/images/invitationIllustration/3x.png';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const LeftPaneBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));

const ResultsList = styled(List)({
  paddingTop: 0,
  '& .MuiListItem-root': {
    paddingTop: 0,
    paddingBottom: 0
  }
});

const StyledSpan = styled('span')({
  color: '#E8446B',
  fontSize: 20
});

const ReceiveItemButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2)
}));

const NoRecordsBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
}));



export interface IProps {
  onClose: () => void;
  onSelect: (returnAuthorizationId: string) => void;

  onClickReceiveUnknownSerialNumber: (serialNumber: string) => void;
}

export const FC: React.FC<IProps> = props => {
  const [fnQuery, tuple] = dataService.useLookUpReturnAuthorization();
  const form = useForm<types.IFormState>({
    defaultValues: {
      value: ''
    }
  });
  const [records, setRecord] = React.useState<dataService.IRecord[]>([]);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      const records = await fnQuery(formState);
      setRecord(records);
    },
    [fnQuery]
  );



  const watch = useWatch({
    control: form.control
  });

  const debouncedProcessSubmit = React.useMemo(
    () => _.debounce(processSubmit, 1000),
    [processSubmit]
  );

  React.useEffect(
    () => {
      const value = watch.value ?? '';
      if (value.length >= 4) {
        debouncedProcessSubmit({ value });
      }
    },
    [
      debouncedProcessSubmit,
      watch.value
    ]
  );



  const completedSearch = tuple.called && !tuple.loading;
  const noRecordsFound = React.useMemo(
    () => _.isEmpty(records) && completedSearch && !!watch.value,
    [
      records,
      completedSearch,
      watch.value
    ]
  );

  return (
    <FormModal.FC
      isDoubleWide
      title="FIND AN RMA"
      isLoading={tuple.loading}
      onClose={props.onClose}
      onSubmit={form.handleSubmit(processSubmit)}>
      <Grid
        container
        alignItems="center"
        columnSpacing={5}>
        <Grid
          item
          xs={12}
          md={6}>
          <LeftPaneBox>
            <Typography sx={{
              fontSize: 16,
              color: '#3A3B3D'
            }}>
              Locate the information you need to find an RMA
            </Typography>

            <Typography sx={{
              fontSize: 14,
              color: '#89898B'
            }}>
              Enter an IMEI, RMA, or reference number to find the correct Tier 1 record.
            </Typography>

            <ResponsiveImage.FC urlSet={[
              imgInvitationIllustration,
              imgInvitationIllustration2x,
              imgInvitationIllustration3x
            ]} />
          </LeftPaneBox>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}>
          <Stack spacing={1}>
            <Typography sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: 13,
              color: '#3A3B3D'
            }}>
              IMEI/RMA/RMA Reference Number <StyledSpan>*</StyledSpan>
            </Typography>

            <Typography sx={{
              fontSize: 12,
              color: '#89898B'
            }}>
              Enter an IMEI/RMA/Reference number to locate the record.  Results will be shown after 4 or more characters have been entered.
            </Typography>

            <ControlledTextField
              fieldKey="value"
              formControl={form.control} />

            <Stack
              spacing={0}
              sx={{ paddingLeft: '12px' }}>
              {tuple.called && !tuple.loading && (
                <Typography sx={{
                  fontSize: 12,
                  color: '#616264'
                }}>
                  Results
                </Typography>
              )}

              <ResultsList>
                {_.map(records, (record, index) => (
                  <ListItem
                    disableGutters
                    key={record.id + index.toString()}>
                    <ListItemButton
                      onClick={() => {
                        BulkReceiveSpecificReturnAuthorizationsModal.open({
                          lines: record.items
                        });
                        props.onClose();
                      }}>
                      <ListItemIcon>
                        <SwapHoriz color="primary" />
                      </ListItemIcon>

                      <ListItemText primary={record.identifier} />
                    </ListItemButton>
                  </ListItem>
                ))}

                {noRecordsFound && (
                  <NoRecordsBox>
                    <Typography
                      sx={{
                        fontSize: 12,
                        paddingLeft: '4px',
                        color: '#CC264F',
                        alignSelf: 'flex-start'
                      }}>
                      No associated records found
                    </Typography>

                    <ReceiveItemButton
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => {
                        const value = watch.value ?? '';
                        if (appValidationUtil.reImei.test(value)) {
                          props.onClickReceiveUnknownSerialNumber(watch.value ?? '');
                        } else {
                          alert("Cannot receive an invalid IMEI. An IMEI is a string of 15 numbers.");
                        }
                      }}>
                      Receive Item
                    </ReceiveItemButton>
                  </NoRecordsBox>
                )}
              </ResultsList>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </FormModal.FC>
  );
};
